import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { VideoEnvironmentProps } from '../../cvp-webgames-core/Components/Environments/React/VideoEnvironment';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { SuccessType } from '../general/general-scenario-stuff';
import { Windows } from '../../misc/tools';
import VortexUI, { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import VortexJobAidUI from '../../components/ui/VortexJobAidUI';

export default class DamonJones extends BasePracticeScenario {
	
	public tempScoreOnce: boolean;
	public didBVM: boolean;

	public get maxScore(): number {
		return 56100;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500; 
		var setupFiberOpticsPoints = 1000;
		var cricPoints = 1400;
		var correctAttempt = 32700;
		var noBVM = 2000;

		if(this.didBVM){ // If they performed BVM, then the 2000 points were already awarded during bvm
		return assessPatientRemoveDenturesPoints +
			setupFiberOpticsPoints +
			cricPoints +
			correctAttempt;
		} else { // if they did not perform BVM, the 2000 points will be added here instead to still get the perfect score
			return assessPatientRemoveDenturesPoints +
			setupFiberOpticsPoints +
			cricPoints +
			correctAttempt +
			noBVM;
		}
	}

	public get totalNumberOfQuestions(): number {
		return 5; 
	}

	public get currentLevel(): number {
		return 1;
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 160,
			bloodPressureDenominator: 90,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 100,
			heartRate: 110,
			heartRateMax: 150,
			respirationRate: 22,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time for Damon Jones. He has expired. Review the debrief and then try again.', true);
			}
		}, context.sideMenuId);
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'Although bag valve mask ventilation is successful and it will get you back to the \"green zone\" with a patient airway, the physician needed the patient to be intubated for treatment and diagnostic imaging.',
				'Intubation is best optimized with the largest ET tube that is clinically appropriate. The optimal ET tube for this patient is size 8.0.',
				'Because the patient has eaten, aspiration is likely with the bag valve mask.',
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}	

	public getDebriefVortex(): DebriefVortex { 
		return {
			successItems: [				
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		this.didBVM = true;
		this.showTempSuccessPopUpAfterAuscultate('The patient is in the green zone. The ED physician needs the patient to be intubated for treatment and diagnostic imaging.', VortexUICurrentAttempt.BVM, 'Temporary Success');
	}

	public onETTFinished(): void {
		this.addScore(this.predeterminedPoints); 
		this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'You established a stable, patent airway for Damon Jones through intubation. Mr. Jones was apneic for ' + this.apneaTimeElapsed + ' while you managed his airway. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. You now have access to the supraglottic airway kit in your tool bag. Review the debrief to help you prepare for additional scenarios.', 'Good Job on this Scenario', './assets/images/success/SGA.png');		
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'His larynx is free and mobile. You can palpate the thyroid and cricoid cartilage as well as the cricothyroid membrane.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 4 finger breadths (6 cm), Hyoid to Thyroid Notch Distance: 2 finger breadths (3 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'You see the patient moving his chin to his chest.'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient doesn’t have dentures.',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'Yes'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				imgUrl: './assets/images/assess-airway-images/Class1.png',
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4 cm)'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			Patient name is Damon Jones. 32-year-old male, 72 inches, 120 kilograms. 
		// 			He was initially cooperative and allowed us to place an IV, labs, and x-ray. 
		// 			But, now has become disruptive. His K is a 4.2 and he ate two hours ago. 
		// 			He came in with a shoulder dislocation due to a fall from a ladder and is 
		// 			high on methamphetamines. He is otherwise healthy and has no comorbidities. 
		// 			There is clear auscultation, no rales or wheezes.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario1 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 			The patient is combative and threatening to attack the nursing staff. 
		// 			We just gave him 5 milligrams of haloperidol IM. Can you please intubate 
		// 			this patient so that I can perform the reduction and acquire further diagnostic imaging? 
		// 			I’ll manage the	hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario1 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DamonJones,
		// 			text: `
		// 				Get away from me.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario1 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `Damon Jones came in with a shoulder dislocation due to a fall from a ladder and is high on methamphetamines. 
		// 		Five mg of haloperidol IM has been administered. He is otherwise healthy with no comorbidities. Clear to auscultation, no rales or wheezes.`,
		// 		b: `
		// 			Age: 32<br />
		// 			Height (in): 72 in<br />
		// 			Weight (kg): 120 kg<br />
		// 			Potassium (K): 4.2<br />
		// 			NPO Status: Ate less than two hours ago<br />
		// 			<br />
		// 			PMH: Otherwise healthy, no comorbidities.<br />
		// 			Comes in with a shoulder dislocation. Patient is high on methamphetamines and threatening to attack the nursing staff. 
		// 			Patient has been given 5 milligrams of Haloperidol IM. The Physician is recommending intubation so that he can perform the reduction.
		// 		`,
		// 		a: `Patient has a dislocated shoulder that has not been reduced. He possibly has other injuries that cannot be assessed due to his behavior.`,
		// 		r: `He needs to be intubated so that the physician can perform the reduction and acquire further diagnostic imaging.`
		// 	}
		// }

		this._id_ScenarioSet(1); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario1 - intro - 53'
				}
			],
			sbarConfig: {
				s: `Damon Jones came in with a shoulder dislocation due to a fall from a ladder and is high on methamphetamines. 
				Five mg of haloperidol IM has been administered. He is otherwise healthy with no comorbidities. Clear to auscultation, no rales or wheezes.`,
				b: `
					Age: 32<br />
					Height (in): 72 in<br />
					Weight (kg): 120 kg<br />
					Potassium (K): 4.2<br />
					NPO Status: Ate less than two hours ago<br />
					<br />
					PMH: Otherwise healthy, no comorbidities.<br />
					Comes in with a shoulder dislocation. Patient is high on methamphetamines and threatening to attack the nursing staff. 
					Patient has been given 5 milligrams of Haloperidol IM. The Physician is recommending intubation so that he can perform the reduction.
				`,
				b508: `
					Age. 32.
					Height. 72 inches.
					Weight. 120 kilograms.
					Potassium. 4.2.
					NPO Status. Ate less than two hours ago.
					Past Medical History. Otherwise healthy no comorbidities.
					Comes in with a shoulder dislocation. Patient is high on methamphetamines and threatening to attack the nursing staff. 
					Patient has been given 5 milligrams of Haloperidol IM. The Physician is recommending intubation so that he can perform the reduction.
					`,
				a: `Patient has a dislocated shoulder that has not been reduced. He possibly has other injuries that cannot be assessed due to his behavior.`,
				r: `He needs to be intubated so that the physician can perform the reduction and acquire further diagnostic imaging.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario1 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader');
			}
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				//constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				//constants.toolbagTools.sga,
				//constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error. ")
			},
			onTeamDebriefed: () => {
				this.debriefLog('Briefed other staff in the room about attempt order');
			},
			onToolsOrganized: () => {
				this.debriefLog('Organized equipment');
			}
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario1 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction Set") 
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario1 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Adjusted Bed");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario1 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario1 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario1 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario1 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario1 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment");
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart");
			}
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_',
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenate: You did not pre-oxygenate the patient.") // will probably get an additional text description
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupAuscultate();
	}

	public getDefaultSa02() {
		return 100
	}

	public getDefaultApneaTime() {
		return 210;
	}

	private _setupBvm() {
		this.procedures.bvm.config.isTempSuccess = () => {
			return true
		}
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',			
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded : boolean) => {
				if(!this.tempScoreOnce) {
					this.addScore(2000); // Temporary Success Score given after final selection is made
					this.tempScoreOnce = true;
				};
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			onProcedureAttempted: (attemptcount: number) => {
				this.bvmAttemptCount= attemptcount;
			},		
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.isAttemptSuccessful = () => {
			return true;
		}
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade1.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''					
				}
			},
			onProcedureAttempted: (attemptcount: number) => {
				this.ettAttemptCount= attemptcount;
			},
			onEttInstrumentInsertionNextClicked: (selectedIndex: number) => {
				this.addScore(2000); // Point award after making instrument selection for airway insertion, no check for repeat because its impossible to answer incorrectly				
			},			
		};
		
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario1/daarc-scenario1-damonjones-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes;
				}
			},
			ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked
				},
				auscultateHoverOverMessages:  () => {
					return this.hoverBreathYes;
				}
			},
			sgaConfig: { //not an accessible tool during scenario
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				checkRiseAndTubeHumidificationText: () => {
					return this.auscFailed
				},
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked;
				},
				auscultateHoverOverMessages:  () => {
					return this.hoverBreathNo
				}
			},
			cricConfig: { //not an accessible tool during scenario
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				checkRiseAndTubeHumidificationText: () => {
					return this.auscSuccess
				},
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}
}